// ==================================================================================
// Digital Approval System (DAS) - useRequestsReport hook
// ==================================================================================
// Server-side paginated/sorted/filtered data source for the five request-
// lifecycle reports. Holds the query (page/pageSize/sort/filters) in state and
// refetches whenever it changes — the grid never loads the full dataset.
// ==================================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { reportsService } from 'api/services/das/reportsService';
import type {
  ReportType,
  RequestReportQuery,
  RequestReportRow,
} from 'api/types/das/reports.types';

export interface ReportFilters {
  searchTerm?: string;
  requestTypeId?: string;
  status?: string;
  requesterUserId?: string;
  approverUserId?: string;
  submittedFrom?: string;
  submittedTo?: string;
  minValue?: number;
  maxValue?: number;
}

export interface ReportPagination {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const DEFAULT_PAGE_SIZE = 25;

export const useRequestsReport = (reportType: ReportType) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<string>('CreatedDate');
  const [sortDescending, setSortDescending] = useState<boolean>(true);
  const [filters, setFilters] = useState<ReportFilters>({});

  const [rows, setRows] = useState<RequestReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPagination>({
    currentPage: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query: RequestReportQuery = useMemo(
    () => ({
      reportType,
      page,
      pageSize,
      sortBy,
      sortDescending,
      ...filters,
    }),
    [reportType, page, pageSize, sortBy, sortDescending, filters]
  );

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportsService.getRequestsReport(query);
      setRows(result.items || []);
      setPagination({
        currentPage: result.page,
        pageSize: result.pageSize,
        totalPages: result.totalPages,
        totalItems: result.totalCount,
        hasNextPage: result.hasNextPage,
        hasPreviousPage: result.hasPreviousPage,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load report';
      setError(message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Toggle sort on a column: same column flips direction, new column starts desc.
  const toggleSort = useCallback((column: string) => {
    setSortBy((prevColumn) => {
      if (prevColumn === column) {
        setSortDescending((prev) => !prev);
        return prevColumn;
      }
      setSortDescending(true);
      return column;
    });
    setPage(1);
  }, []);

  // Apply new filters and reset to page 1.
  const applyFilters = useCallback((next: ReportFilters) => {
    setFilters(next);
    setPage(1);
  }, []);

  const changePageSize = useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  return {
    rows,
    pagination,
    loading,
    error,
    query,
    // controls
    sortBy,
    sortDescending,
    setPage,
    changePageSize,
    toggleSort,
    filters,
    applyFilters,
    refetch: fetchReport,
  };
};
